import { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  CategoryScale,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { startOfISOWeek, addDays, getISOWeek } from "date-fns";
import "chartjs-adapter-date-fns";
import { sv } from "date-fns/locale";
import type { ChartDataset } from "chart.js";
import { buildDateMap } from "./ChartUtils";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  CategoryScale
);

type LineDataset = ChartDataset<"line", number[]> & {
  customBreakdown?: any[];
};
interface CoachChartAllCoachesProps {
  coaches: {
    coachName: string;
    rows: any[];
  }[];
  startDate: string;
  endDate: string;
}

const orderedDays = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const colors = [
  "#3F9C30",
  "#309C49",
  "#309C6B",
  "#30849C",
  "#30629C",
  "#303F9C",
  "#6B309C",
  "#8C309C",
];

export function CoachChartAllCoaches({
  coaches,
  startDate,
  endDate,
}: CoachChartAllCoachesProps) {
  const [groupByWeek, setGroupByWeek] = useState(true);
  const [chartMax, setChartMax] = useState<number | null>(null);

  const dateMap = buildDateMap(startDate, endDate);

  const weekDateMap = new Map<number, Date>();

  dateMap.forEach((date, key) => {
    const [weekStr] = key.split("-");
    const week = Number(weekStr);

    // keep the earliest date we see for that week (Monday wins)
    if (!weekDateMap.has(week) || date < weekDateMap.get(week)!) {
      weekDateMap.set(week, date);
    }
  });

  // ---- Collect weeks ----
  const weeks = Array.from(
    new Set(
      coaches.flatMap((c) =>
        c.rows.filter((r) => r.type === "row").map((r) => r.timespan)
      )
    )
  );

  const weeksSorted = weeks.sort((a, b) => {
    const [ya, wa] = a.match(/\d+/g)!.map(Number);
    const [yb, wb] = b.match(/\d+/g)!.map(Number);
    return ya !== yb ? ya - yb : wa - wb;
  });

  // ---- X labels ----
  const dailyLabels: Date[] = [];
  const weeklyLabels = weeksSorted;

  dateMap.forEach((date, key) => {
    const [weekStr] = key.split("-");
    const week = Number(weekStr);
    if (weeks.includes(week)) {
      dailyLabels.push(date);
    }
  });
  dailyLabels.sort((a, b) => a.getTime() - b.getTime());

  // ---- Build datasets per coach ----
  const datasets: LineDataset[] = coaches.map((coach, idx) => {
    const weeklyRows = coach.rows.filter((r) => r.type === "row");

    const dailyData: number[] = [];
    const dailyBreakdown: any[] = [];

    weeklyRows.forEach((row) =>
      orderedDays.forEach((day) => {
        const cell = row[day];
        dailyData.push(cell?.booked ?? 0);
        dailyBreakdown.push(cell?.memberTypes ?? []);
      })
    );

    const weeklyData: number[] = [];
    const weeklyBreakdown: any[] = [];

    weeks.forEach((week) => {
      let sum = 0;
      const totals: Record<string, number> = {};

      weeklyRows
        .filter((r) => r.timespan === week)
        .forEach((r) =>
          orderedDays.forEach((d) => {
            const cell = r[d];
            sum += cell?.booked ?? 0;
            cell?.memberTypes?.forEach((m: any) => {
              totals[m.memberType] = (totals[m.memberType] ?? 0) + m.count;
            });
          })
        );

      weeklyData.push(sum);
      weeklyBreakdown.push(
        Object.entries(totals).map(([memberType, count]) => ({
          memberType,
          count,
        }))
      );
    });

    return {
      label: coach.coachName,
      data: groupByWeek ? weeklyData : dailyData,
      customBreakdown: groupByWeek ? weeklyBreakdown : dailyBreakdown,
      borderColor: colors[idx % colors.length],
      backgroundColor: colors[idx % colors.length] + "55",
      borderWidth: 2,
      tension: 0.1,
    };
  });

  // ---- Total dashed line ----
  const totalData = datasets[0]?.data.map((_, i) =>
    datasets.reduce((sum, d) => sum + (d.data[i] ?? 0), 0)
  );

  datasets.unshift({
    label: "Totalt",
    data: totalData,
    borderColor: "#30389c",
    backgroundColor: "#30389c55",
    borderDash: [6, 4],
    borderWidth: 2,
    pointRadius: 3,
    tension: 0.1,
  });
  const numericValues = datasets
    .flatMap((d) => d.data)
    .filter((v): v is number => v != null && !isNaN(v));

  const maxValue = Math.max(...numericValues, 10);

  useEffect(() => {
    setChartMax(Math.ceil(maxValue * 1.1));
  }, [maxValue, groupByWeek]);

  const rangeOptions = [0.25, 0.5, 0.75, 1.1, 1.5].map((factor) => ({
    label: `${Math.round(maxValue * factor)}`,
    value: Math.round(maxValue * factor),
  }));

  if (!coaches.length) return <div>No data</div>;
  return (
    <div className="small-coach-chart text-m">
      <div className="chart-controls">
        <span style={{ marginRight: "auto" }}>Bokningar per tränare</span>
        <div className="chart-max-select">
          <label>Maxvärde:</label>
          <select
            className="chart-max-select-dropdown"
            value={chartMax ?? ""}
            onChange={(e) =>
              setChartMax(e.target.value ? Number(e.target.value) : null)
            }
          >
            <option value={Math.ceil(maxValue * 1.1)}>Auto</option>
            {rangeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="chart-view-toggle">
          Visa per vecka
          <label className="toggle text-m">
            <input
              type="checkbox"
              checked={groupByWeek}
              onChange={(e) => setGroupByWeek(e.target.checked)}
            />
            <span className="slider round" />
          </label>
        </div>
      </div>

      <Line
        data={{
          labels: groupByWeek ? weeklyLabels : (dailyLabels as any),
          datasets,
        }}
        options={{
          scales: {
            x: groupByWeek
              ? {
                  type: "category",
                  title: {
                    display: true,
                    text: "Vecka",
                  },
                }
              : {
                  type: "time",
                  time: {
                    unit: "day",
                    tooltipFormat: "EEE dd/MM",
                    displayFormats: {
                      day: "EEE dd/MM",
                    },
                  },
                  adapters: {
                    date: { locale: sv },
                  },
                  title: {
                    display: true,
                    text: "Datum",
                  },
                },
            y: {
              beginAtZero: true,
              min: 0,
              max: Number(chartMax),
              ticks: {
                stepSize: 1,
                precision: 0,
              },
              title: {
                display: true,
                text: "Bokningar",
              },
            },
          },

          plugins: {
            tooltip: {
              callbacks: {
                label: (ctx: any) => {
                  const breakdown =
                    ctx.dataset.customBreakdown?.[ctx.dataIndex];
                  if (!breakdown)
                    return `${ctx.dataset.label}: ${ctx.parsed.y}`;

                  return [
                    `${ctx.dataset.label}: ${ctx.parsed.y}`,
                    ...breakdown.map((b: any) => `${b.memberType}: ${b.count}`),
                  ];
                },
              },
            },
          },
        }}
      />
    </div>
  );
}
